// Main browser entrypoint.
//
// Strategy:
// 1) Load critical UX modules immediately.
// 2) Defer non-critical modules until idle time or until their section is near viewport.
//
// This keeps first-load JS work small while preserving feature behavior.
import './modules/reveal-on-scroll.js';
import './modules/theme-toggle.js';

const supportsIntersectionObserver = 'IntersectionObserver' in window;

// Run a loader during browser idle time (with a timeout fallback) so non-urgent
// code does not compete with first paint.
function loadWhenIdle(loader, timeout = 1600) {
	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(() => {
			loader();
		}, { timeout });
		return;
	}

	window.setTimeout(() => {
		loader();
	}, 1);
}

// Load a module the first time a section intersects the viewport.
// Useful for feature code that is only needed once the user scrolls near that area.
function loadOnFirstIntersection(selector, loader, options = {}) {
	const target = document.querySelector(selector);
	if (!target) {
		// Section is missing on this page variant; skip silently.
		return;
	}

	if (!supportsIntersectionObserver) {
		// Older browsers: eagerly load to keep behavior functional.
		loader();
		return;
	}

	let hasLoaded = false;
	const observer = new IntersectionObserver(
		(entries) => {
			// Fire once when any observed entry is visible.
			const isVisible = entries.some((entry) => entry.isIntersecting);
			if (!isVisible || hasLoaded) {
				return;
			}

			hasLoaded = true;
			observer.disconnect();
			loader();
		},
		{
			rootMargin: options.rootMargin || '0px 0px -12% 0px',
			threshold: options.threshold || 0.01,
		}
	);

	observer.observe(target);
}

// Footer year is not critical for initial rendering.
loadWhenIdle(() => import('./modules/footer-year.js'));

// Feature modules are scoped by section to reduce first-load script work.
loadOnFirstIntersection('#services', () => import('./modules/service-card-pointer.js'));
loadOnFirstIntersection('#tech-stack', () => import('./modules/tech-tooltip.js'));
loadOnFirstIntersection('#projects', () => import('./modules/project-showcase.js'));
loadOnFirstIntersection(
	'#contact',
	() => {
		// Both modules are tied to the contact area, so load together.
		import('./modules/contact-rotating-text.js');
		import('./modules/logo-shapes-scroll.js');
	},
	{ rootMargin: '0px 0px 18% 0px' }
);
